import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  GetRegistrationSteps,
  GetRegistrationStepsResponse,
  GetCandidatesByColumnIdResponse,
  GetCandidatesByColumnId,
  UpdateCandidateRegistrationStep,
  UpdateCandidateRegistrationStepDTO, UpdateCandidateRegistrationStepResponseDTO,
} from "../api";

type Props<T> = {
  onSuccess?: (data: T) => void;
  onError?: (error: any) => void;
};

export const useGetRegistrationStep = (
  props?: Props<GetRegistrationStepsResponse>
) => {
  const { data, isLoading, isError } = useQuery<GetRegistrationStepsResponse>(
    ["registrationStep"],
    () => GetRegistrationSteps(),
    {
      onSuccess: props?.onSuccess,
      onError: props?.onError,
    }
  );
  return { data, isLoading, isError };
};

export const useGetCandidatesByColumnId = (
  columnId: number,
  search: string | null,
  filters: { qualification?: string[] },
  props?: Props<InfiniteData<GetCandidatesByColumnIdResponse>>
) => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<GetCandidatesByColumnIdResponse>(
    ["candidates", columnId, search, filters],
    ({ pageParam = 1 }) => GetCandidatesByColumnId(columnId, search, filters, pageParam),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.info.next_page) {
          return lastPage.info.next_page;
        }
        return undefined;
      },
      onSuccess: props?.onSuccess,
      onError: props?.onError,
    }
  );
  return {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useUpdateCandidateRegistrationStep = (
  props?: Props<UpdateCandidateRegistrationStepDTO>
) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading, isError, error, data } = useMutation(
    UpdateCandidateRegistrationStep,
    {
      onSuccess: async(data: UpdateCandidateRegistrationStepResponseDTO) => {
        // Resolve once both columns are refetched so optimistic overrides can be dropped safely
        await Promise.all([
          queryClient.invalidateQueries(["candidates", data.new_step_id]),
          queryClient.invalidateQueries(["candidates", data.previous_step_id]),
        ]);
        props?.onSuccess?.(data);
      },
      onError: (error) => {
        queryClient.invalidateQueries(["candidates"]);
        props?.onError?.(error);
      },
    }
  );

  return { mutateAsync, isLoading, isError, error, data };
};
